import type { TaskRelatedEntity } from './task';

/** Default-off rollout gate for configurable claim states (CUR-473). */
export const ENABLE_CLAIM_STATES_FLAG = 'enable-claim-states';

export const QUESTIONNAIRE_MAPPED_FIELDS = {
    CAUSE_OF_LOSS: 'causeOfLoss',
    /**
     * The insured vehicle's registration (CUR-1049). Set this internal name on the
     * lodgement question that asks for it and the post-create workflow matches the
     * answer to a fleet vehicle, which is what puts the claim in the fleet portal.
     */
    VEHICLE_REGISTRATION: 'vehicleRegistration',
    DATE_OF_LOSS: 'dateOfLoss',
    ADDRESS_LINE_1_OF_LOSS: 'addressLine1OfLoss',
    ADDRESS_LINE_2_OF_LOSS: 'addressLine2OfLoss',
    SUBURB_OF_LOSS: 'suburbOfLoss',
    STATE_OF_LOSS: 'stateOfLoss',
    POSTCODE_OF_LOSS: 'postcodeOfLoss',

    /**
     * This logic is available only if is-bordereau-report-enabled feature flag is enabled.
     * For now it applies only for Enthusiast as workaround to create bordereau report.
     */
    IS_VEHICLE_UNDER_FINANCE: 'isVehicleUnderFinance',
    THIRD_PARTY_DRIVER_FIRST_NAME: 'thirdPartyDriverFirstName',
    THIRD_PARTY_DRIVER_LAST_NAME: 'thirdPartyDriverLastName',
    THIRD_PARTY_DRIVER_ADDRESS_LINE_1: 'thirdPartyDriverAddressLine1',
    THIRD_PARTY_DRIVER_ADDRESS_LINE_2: 'thirdPartyDriverAddressLine2',
    THIRD_PARTY_DRIVER_SUBURB: 'thirdPartyDriverSuburb',
    THIRD_PARTY_DRIVER_STATE: 'thirdPartyDriverState',
    THIRD_PARTY_DRIVER_POSTCODE: 'thirdPartyDriverPostcode',
    THIRD_PARTY_DRIVER_EMAIL: 'thirdPartyDriverEmail',
    THIRD_PARTY_DRIVER_PHONE: 'thirdPartyDriverPhone',
    THIRD_PARTY_DRIVER_VEHICLE_MAKE: 'thirdPartyDriverVehicleMake',
    THIRD_PARTY_DRIVER_VEHICLE_MODEL: 'thirdPartyDriverVehicleModel',
    THIRD_PARTY_DRIVER_REGISTRATION_NUMBER: 'thirdPartyDriverRegistrationNumber',

    THIRD_PARTY_DRIVER_INSURER_NAME: 'thirdPartyDriverInsurerName',
    THIRD_PARTY_DRIVER_CLAIM_NUMBER: 'thirdPartyDriverClaimNumber',
} as const;

/**
 * Well-known childId binding the hardcoded lodgement first-page (policy holder)
 * address to the public address-suggestion procedures (CUR-406). Unlike ADDRESS
 * questionnaire children, the first page exists on every live lodgement
 * questionnaire, so the server accepts this sentinel without a definition scan.
 */
export const LODGEMENT_POLICY_HOLDER_ADDRESS_CHILD_ID = 'lodgement-policy-holder-address';

export const CLAIM_TASKS = {
    updateClientOnProgress: {
        internalName: 'update_client_on_progress',
        title: 'Update the client on the progress of the claim',
        description: 'Contact the client to provide a progress update on their claim within the required timeframe.',
        relatedEntity: 'claim',
    },
} as const satisfies Record<
    string,
    {
        internalName: string;
        title: string;
        description: string;
        relatedEntity: TaskRelatedEntity;
    }
>;

export const CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE = {
    BUSINESS: 'BUSINESS',
    CALENDAR: 'CALENDAR',
} as const;

type ClaimClientUpdateDayIntervalType =
    (typeof CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE)[keyof typeof CLAIM_CLIENT_UPDATE_DAY_INTERVAL_TYPE];

export type ClaimClientUpdateConfiguration = {
    isEnabled: boolean;
    intervalDays: number;
    dueSoonDays: number;
    dayIntervalType: ClaimClientUpdateDayIntervalType;
    notificationEmail?: string | null;
    /**
     * Label shown for the client-update widget on the claim header (and the
     * claims list column). Configurable so, for example, Australian teams can
     * call it "GICOP update". Optional because configs saved before this field
     * existed won't include it — fall back to {@link DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION.headerWidgetLabel}.
     */
    headerWidgetLabel?: string;
    task: {
        title: string;
        description: string;
    };
    milestone: {
        labelWhenCompleted: string;
    };
};

export const DEFAULT_CLAIM_CLIENT_UPDATE_CONFIGURATION: ClaimClientUpdateConfiguration = {
    isEnabled: true,
    intervalDays: 20,
    dueSoonDays: 2,
    dayIntervalType: 'BUSINESS',
    notificationEmail: null,
    headerWidgetLabel: 'Client update',
    task: {
        title: CLAIM_TASKS.updateClientOnProgress.title,
        description: CLAIM_TASKS.updateClientOnProgress.description,
    },
    milestone: {
        labelWhenCompleted: 'Client updated on progress',
    },
};

/**
 * Which public lodgement route a claim is being lodged from. Both surfaces render the
 * same questionnaire, so a card that navigates the claimant off-site (VEHICLE_INSPECTION)
 * must tell the server which one to send them back to — the provider's completion
 * redirect is built server-side from typed route parts, never from a client-supplied URL.
 *
 * `lodgement-lookup` is the vehicle/driver-lookup flow (CUR-217), which is blocked on
 * production; `lodgement` is the live flow and the default for any caller that omits it.
 */
export const LODGEMENT_SURFACES = ['lodgement', 'lodgement-lookup'] as const;

export type LodgementSurface = (typeof LODGEMENT_SURFACES)[number];

export const DEFAULT_LODGEMENT_SURFACE: LodgementSurface = 'lodgement';
